import { c as _c } from "react/compiler-runtime";
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { Button } from '@/components/form/baseInputs/Button';
import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { EditableHeader } from '@/components/form/EditableHeader/EditableHeader';
import { AlertTriangleIcon } from '@/components/icons/AlertTriangleIcon';
import { Download01Icon } from '@/components/icons/Download01Icon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useSalesforceBrand } from '@/modules/salesforce/brand';
import { COLORS } from '@/styles/tokens/colors';
import { UnreachableError } from '@/utils/errors';
import type { AnswerReviewSaveState, AnswerReviewWorkbenchProps } from './AnswerReviewWorkbench.types';
import { FormSubmissionName } from './FormSubmissionName';
function SaveStatus(t0) {
  const $ = _c(11);
  const {
    state,
    onRetrySave,
    onResolveConflict
  } = t0;
  switch (state.kind) {
    case "saved":
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Typography role="status" aria-live="polite" variant="body-xs" whiteSpace="nowrap">Changes are auto-saved</Typography>;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        return t1;
      }
    case "saving":
      {
        let t1;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Stack role="status" aria-live="polite" direction="row" alignItems="center" gap={1}><CircularProgress size={14} /><Typography variant="body-xs">Saving changes</Typography></Stack>;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case "error":
    case "conflict":
      {
        const isConflict = state.kind === "conflict";
        const action = isConflict ? onResolveConflict : onRetrySave;
        let t1;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <AlertTriangleIcon size={16} color={COLORS.FUNCTIONAL.ERROR.DEFAULT} />;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        let t2;
        if ($[3] !== state.message) {
          t2 = <Typography variant="body-xs">{state.message}</Typography>;
          $[3] = state.message;
          $[4] = t2;
        } else {
          t2 = $[4];
        }
        let t3;
        if ($[5] !== action || $[6] !== isConflict) {
          t3 = action && <Button variant="secondary" size="xs" onClick={action}>{isConflict ? "Resolve conflict" : "Try again"}</Button>;
          $[5] = action;
          $[6] = isConflict;
          $[7] = t3;
        } else {
          t3 = $[7];
        }
        let t4;
        if ($[8] !== t2 || $[9] !== t3) {
          t4 = <Stack role="alert" direction="row" alignItems="center" justifyContent="center" flexWrap="wrap" gap={1}>{t1}{t2}{t3}</Stack>;
          $[8] = t2;
          $[9] = t3;
          $[10] = t4;
        } else {
          t4 = $[10];
        }
        return t4;
      }
    default:
      {
        throw new UnreachableError({
          case: state,
          message: "Unknown answer save state"
        });
      }
  }
}
export function AnswerReviewHeader(t0) {
  const $ = _c(47);
  const {
    state,
    formName,
    formSubmissionId,
    canEditFormName,
    onClose,
    onRetrySave,
    onResolveConflict,
    onDone,
    isDonePending,
    onAddCitation,
    onAnswerChange,
    exportHref,
    onExportPdf,
    onExportWord,
    onExportSalesforce,
    isSalesforceExported,
    isExportWordPending
  } = t0;
  const brand = useSalesforceBrand();
  const review = state.kind === "ready" ? state.review : undefined;
  const isEditable = review?.mode === "editable";
  let formNameContent;
  if (formName && formSubmissionId) {
    const t1 = !canEditFormName;
    let t2;
    if ($[0] !== formName || $[1] !== formSubmissionId || $[2] !== t1) {
      t2 = <FormSubmissionName submissionId={formSubmissionId} name={formName} disabled={t1} />;
      $[0] = formName;
      $[1] = formSubmissionId;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    formNameContent = t2;
  } else {
    if (formName) {
      let t1;
      if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          component: "span",
          noWrap: true
        };
        $[4] = t1;
      } else {
        t1 = $[4];
      }
      let t2;
      if ($[5] !== formName) {
        t2 = <EditableHeader value={formName} onChange={_temp} disabled={true} typographyVariant="title-l" typographyProps={t1} testId="form-submission-name" />;
        $[5] = formName;
        $[6] = t2;
      } else {
        t2 = $[6];
      }
      formNameContent = t2;
    }
  }
  const headingLabel = formName ?? "Form review";
  let t1;
  if ($[7] !== formNameContent || $[8] !== headingLabel) {
    t1 = formNameContent ?? <Typography component="span" variant="title-l">{headingLabel}</Typography>;
    $[7] = formNameContent;
    $[8] = headingLabel;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== review?.mode) {
    t2 = review?.mode === "completed" && <Box component="span" px={1} py={0.25} borderRadius={3} bgcolor={COLORS.TEAL[50]}><Typography component="span" variant="body-xs">Completed</Typography></Box>;
    $[10] = review?.mode;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== review?.mode) {
    t3 = review?.mode === "readOnly" && <Box component="span" px={1} py={0.25} borderRadius={3} bgcolor={COLORS.GRAY[100]}><Typography component="span" variant="body-xs">Read only</Typography></Box>;
    $[12] = review?.mode;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== headingLabel || $[15] !== t1 || $[16] !== t2 || $[17] !== t3) {
    t4 = <Stack role="heading" aria-level={1} aria-label={headingLabel} direction="row" alignItems="center" gap={1}>{t1}{t2}{t3}</Stack>;
    $[14] = headingLabel;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const titleContent = t4;
  let t5;
  let t6;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      xs: "column",
      md: "row"
    };
    t6 = {
      xs: "stretch",
      md: "center"
    };
    $[19] = t5;
    $[20] = t6;
  } else {
    t5 = $[19];
    t6 = $[20];
  }
  let t7;
  if ($[21] !== titleContent) {
    t7 = <Box minWidth={0} flex={1}>{titleContent}</Box>;
    $[21] = titleContent;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== isEditable || $[24] !== onAddCitation || $[25] !== onAnswerChange || $[26] !== onResolveConflict || $[27] !== onRetrySave || $[28] !== review) {
    t8 = isEditable && (!onAddCitation || onAnswerChange || review.saveState.kind !== "saved") && <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5} flex={1} minWidth={0}><SaveStatus state={review.saveState} onRetrySave={onRetrySave} onResolveConflict={onResolveConflict} /></Stack>;
    $[23] = isEditable;
    $[24] = onAddCitation;
    $[25] = onAnswerChange;
    $[26] = onResolveConflict;
    $[27] = onRetrySave;
    $[28] = review;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== brand || $[31] !== exportHref || $[32] !== isDonePending || $[33] !== isEditable || $[34] !== isExportWordPending || $[35] !== isSalesforceExported || $[36] !== onClose || $[37] !== onDone || $[38] !== onExportPdf || $[39] !== onExportSalesforce || $[40] !== onExportWord || $[41] !== review) {
    t9 = (review || onClose) && <Stack direction="row" alignItems="center" flexWrap="wrap" justifyContent="flex-end" flex={1} gap={1}><ButtonWithPopover size="sm" variant="secondary" startIcon={Download01Icon} label="Export" aria-label="Export" loading={isExportWordPending} disabled={!exportHref && !onExportSalesforce || isExportWordPending} popperProps={{
        anchorOrigin: {
          horizontal: "left",
          vertical: "bottom"
        },
        transformOrigin: {
          horizontal: "left",
          vertical: 0
        }
      }}><MenuItem label="Export as PDF" disabled={!exportHref} onClick={() => {
          onExportPdf?.();
          window.open(exportHref, "_blank", "noopener,noreferrer");
        }} /><MenuItem label="Export as Word" onClick={() => onExportWord?.()} loading={isExportWordPending} disabled={!onExportWord} />{onExportSalesforce && <MenuItem label={isSalesforceExported ? `View in ${brand.name}` : `Export to ${brand.name}`} onClick={onExportSalesforce} />}</ButtonWithPopover>{isEditable && onDone && <Button variant="primary" size="sm" loading={isDonePending} disabled={isDonePending} onClick={onDone}>Done</Button>}{!isEditable && onClose && <Button variant="primary" size="sm" onClick={onClose}>Done</Button>}</Stack>;
    $[30] = brand;
    $[31] = exportHref;
    $[32] = isDonePending;
    $[33] = isEditable;
    $[34] = isExportWordPending;
    $[35] = isSalesforceExported;
    $[36] = onClose;
    $[37] = onDone;
    $[38] = onExportPdf;
    $[39] = onExportSalesforce;
    $[40] = onExportWord;
    $[41] = review;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  let t10;
  if ($[43] !== t7 || $[44] !== t8 || $[45] !== t9) {
    t10 = <Stack component="header" direction={t5} alignItems={t6} justifyContent="space-between" gap={1.5} px={2} py={1.5} bgcolor={COLORS.PRIMITIVES.WHITE} borderBottom={`1px solid ${COLORS.GRAY[200]}`} flexShrink={0}>{t7}{t8}{t9}</Stack>;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  return t10;
}
function _temp() {}